import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Send, Loader2, Clock, Users, CheckCircle2, AlertTriangle, Pencil, X } from 'lucide-react';
import { Button } from '../ui/button';
import { API } from '../../utils/kitchenTasks';
import { formatDateFR, formatDateTimeFR } from '../../utils/dateFormat';
import { formatApiError } from '../../services/api';

const STATUS = {
  sent: { label: 'Envoyé', cls: 'text-emerald-700', Icon: CheckCircle2 },
  partial: { label: 'Partiellement envoyé', cls: 'text-amber-700', Icon: AlertTriangle },
  failed: { label: 'Échec', cls: 'text-red-700', Icon: AlertTriangle },
  not_configured: { label: 'Simulé (e-mail non configuré)', cls: 'text-amber-700', Icon: AlertTriangle },
};

const WeeklyEmailPanel = ({ period, kind = 'mise-en-place', title = 'Envoi hebdomadaire du rapport (PDF + récap articles CSV)', sendPeriodLabel = 'Envoyer la période affichée' }) => {
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const load = useCallback(() => axios.get(`${API}/reports/weekly-email/${kind}/status`).then(r => setStatus(r.data.data)).catch(() => {}), [kind]);
  useEffect(() => { load(); }, [load]);

  const sendNow = async (usePeriod) => {
    setSending(true);
    try {
      const params = usePeriod && period?.from && period?.to ? { date_from: period.from, date_to: period.to } : {};
      const res = await axios.post(`${API}/reports/weekly-email/${kind}/send`, null, { params });
      const run = res.data.data;
      if (run.status === 'sent') toast.success(`Rapport envoyé à ${run.sent_count} destinataire${run.sent_count > 1 ? 's' : ''}`);
      else if (run.status === 'not_configured') toast.warning("E-mail non configuré : envoi simulé et journalisé");
      else toast.error(`Envoi incomplet (${run.sent_count}/${run.recipients.length})`);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSending(false); }
  };

  const saveRecipients = async (reset = false) => {
    try {
      const body = reset ? { recipients: null } : { recipients: draft.split(/[,;\n]/).map(s => s.trim()).filter(Boolean) };
      const res = await axios.put(`${API}/reports/weekly-email/${kind}/recipients`, body);
      setStatus(s => ({ ...s, ...res.data.data }));
      setEditing(false);
      toast.success('Destinataires mis à jour');
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!status) return null;
  const last = status.last_run;
  const st = last ? STATUS[last.status] || STATUS.failed : null;
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3" data-testid={`weekly-email-panel-${kind}`}>
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
        <div className="space-y-1.5 text-sm">
          <div className="font-heading text-base font-bold text-gray-900 flex items-center gap-2"><Mail size={16} className="text-neon-pink" />{title}</div>
          <p className="text-gray-600 flex items-center gap-1.5"><Clock size={13} className="text-gray-400" /><span data-testid="weekly-email-schedule">{status.schedule}</span> · semaine passée ({formatDateFR(status.next_period.from)} → {formatDateFR(status.next_period.to)})</p>
          <div className="text-gray-600 flex items-start gap-1.5">
            <Users size={13} className="text-gray-400 mt-0.5" />
            {editing ? (
              <div className="flex-1 space-y-2">
                <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} placeholder="adresse1@…, adresse2@… (comptes existants uniquement)" className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm" data-testid="weekly-email-recipients-input" />
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => saveRecipients(false)} className="h-9 rounded-full bg-gray-900 text-white" data-testid="weekly-email-recipients-save-btn">Enregistrer</Button>
                  <Button size="sm" variant="outline" onClick={() => saveRecipients(true)} className="h-9 rounded-full" data-testid="weekly-email-recipients-reset-btn">Revenir au défaut (Chef + super_admin)</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-9 rounded-full" data-testid="weekly-email-recipients-cancel-btn"><X size={14} /></Button>
                </div>
              </div>
            ) : (
              <span>
                Destinataires : <span className="font-medium text-gray-800" data-testid="weekly-email-recipients">{status.recipients.join(', ') || '—'}</span>
                {status.can_edit_recipients && <button type="button" onClick={() => { setDraft(status.recipients.join(', ')); setEditing(true); }} className="ml-2 inline-flex items-center gap-1 text-xs text-neon-pink hover:underline" data-testid="weekly-email-recipients-edit-btn"><Pencil size={11} />modifier</button>}
              </span>
            )}
          </div>
          {!status.email_configured && <p className="text-amber-700 text-xs flex items-center gap-1" data-testid="weekly-email-not-configured"><AlertTriangle size={12} />E-mail non configuré : les envois sont simulés et journalisés.</p>}
          {last && st && (
            <p className={`text-xs flex items-center gap-1 ${st.cls}`} data-testid="weekly-email-last-run">
              <st.Icon size={12} />Dernier envoi : {st.label} · {formatDateTimeFR(last.finished_at)} · période {formatDateFR(last.period.from)} → {formatDateFR(last.period.to)} · {last.sent_count}/{last.recipients.length} destinataire{last.recipients.length > 1 ? 's' : ''} · {last.trigger === 'cron' ? 'automatique' : 'manuel'}
            </p>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
          <Button variant="outline" onClick={() => sendNow(true)} disabled={sending} className="h-10 rounded-full border-gray-300 text-gray-700" data-testid="weekly-email-send-period-btn">
            {sending ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Send size={15} className="mr-1.5" />}{sendPeriodLabel}
          </Button>
          <Button onClick={() => sendNow(false)} disabled={sending} className="h-10 rounded-full bg-neon-pink hover:bg-neon-pink/90 text-gray-900 font-semibold" data-testid="weekly-email-send-now-btn">
            {sending ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Send size={15} className="mr-1.5" />}Envoyer la semaine passée maintenant
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WeeklyEmailPanel;
